import React, { useState, useEffect } from 'react';
import { BellIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useChangelog } from '../../components/Changelog/ChangelogProvider';
import { ChangelogModal } from '../../ui/atoms';
import { cleanHtmlContent } from '../../components/Changelog/utils';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

const NotificationsPage = () => {
  const [isChangelogModalOpen, setIsChangelogModalOpen] = useState(false);
  const { 
    allChangelogs,
    markChangelogAsSeen, 
    loadAllChangelogs 
  } = useChangelog();

  // Load all changelogs on mount
  useEffect(() => {
    loadAllChangelogs();
  }, [loadAllChangelogs]);

  // Generate notifications from all changelogs  
  console.log('🔍 NotificationsPage - allChangelogs:', allChangelogs);

  const allNotifications = allChangelogs.map((changelog, index) => {
    const lastSeenId = parseInt(localStorage.getItem('last_seen_changelog_id') || '0');
    const isNew = changelog.id > lastSeenId;
    
    return {
      id: `changelog-${changelog.id}`,
      type: 'changelog',
      title: 'Aktualizacje platformy',
      message: cleanHtmlContent(changelog.changelog_excerpt) || 'Sprawdź najnowsze funkcje i ulepszenia platformy',
      time: isNew ? 'Nowe' : formatDisplayDateTime(changelog.date_created, 'pl'),
      isNew: isNew,
      category: changelog.changelog_category && changelog.changelog_category.length > 0 
        ? changelog.changelog_category[0] 
        : 'Aktualizacje',
      data: changelog
    };
  });

  const handleChangelogClick = (notification) => {
    setIsChangelogModalOpen(true);
    if (notification.isNew && notification.data) {
      markChangelogAsSeen(notification.data.id);
    }
  };

  const getNotificationIcon = (type) => {
    // Using neutral bell icon for all notifications
    return <BellIcon className="h-6 w-6 text-gray-400" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Aktualizacje': 'bg-primary-100 text-primary-800',
      'Generowanie': 'bg-green-100 text-green-800',
      'Jakość': 'bg-yellow-100 text-yellow-800',
      'Projekty': 'bg-blue-100 text-blue-800',
      'Dane': 'bg-purple-100 text-purple-800',
      'System': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-medium text-gray-900 mb-2">Powiadomienia</h1>
        <p className="text-gray-600">
          Wszystkie powiadomienia i aktualizacje w jednym miejscu
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            Łącznie: {allNotifications.length} powiadomień
          </span>
          <span className="text-sm text-gray-500">
            Nowych: {allNotifications.filter(n => n.isNew).length}
          </span>
        </div>
        <div className="flex space-x-2">
          <button className="btn btn-sm btn-square btn-secondary">
            <CheckIcon className="h-4 w-4 mr-2" />
            Oznacz jako przeczytane
          </button>
          <button className="btn btn-sm btn-square btn-secondary">
            <TrashIcon className="h-4 w-4 mr-2" />
            Wyczyść wszystkie
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
        {allNotifications.length > 0 ? (
          allNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={notification.type === 'changelog' ? () => handleChangelogClick(notification) : undefined}
              className={`p-6 hover:bg-gray-50 ${
                notification.type === 'changelog' ? 'cursor-pointer' : ''
              } ${notification.isNew ? 'bg-blue-50 border-l-4 border-primary-400' : ''}`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <h3 className={`text-sm font-medium ${
                        notification.isNew ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {notification.title}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getCategoryColor(notification.category)
                      }`}>
                        {notification.category}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        notification.isNew 
                          ? 'bg-red-100 text-red-800' 
                          : 'text-gray-500'
                      }`}>
                        {notification.time}
                      </span>
                      {notification.isNew && (
                        <span className="flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center">
            <BellIcon className="mx-auto h-16 w-16 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              Brak powiadomień
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Wszystkie powiadomienia pojawią się tutaj
            </p>
          </div>
        )}
      </div>

      {/* Changelog Modal */}
      <ChangelogModal 
        isOpen={isChangelogModalOpen} 
        onClose={() => setIsChangelogModalOpen(false)} 
      />
    </div>
  );
};

export default NotificationsPage;
